import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDZ } from '../../../utils/currency';
import Input from '../../../components/ui/Input';

const CartSummary = ({ items, shipping, tax, discount, onApplyPromo }) => {
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const navigate = useNavigate();

  const subtotal = items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  const total = subtotal + shipping + tax - discount;

  const handleApplyPromo = async () => {
    if (!promoCode?.trim()) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const result = await onApplyPromo(promoCode);
      if (!result?.success) {
        setPromoError(result?.message || 'Invalid promo code');
      } else {
        setPromoCode('');
      }
    } catch (error) {
      setPromoError('Failed to apply promo code');
    }
    
    setPromoLoading(false);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div className="holographic-card p-6 rounded-lg space-y-6">
      <h3 className="text-xl font-semibold command-text">Order Summary</h3>
      {/* Promo Code Section */}
      <div className="space-y-3">
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => setPromoCode(e?.target?.value)}
            error={promoError}
            className="flex-1"
          />
          <Button
            variant="outline"
            onClick={handleApplyPromo}
            disabled={!promoCode?.trim() || promoLoading}
            loading={promoLoading}
          >
            Apply
          </Button>
        </div>
        {discount > 0 && (
          <div className="flex items-center space-x-2 text-success">
            <Icon name="CheckCircle" size={16} />
            <span className="text-sm">Promo code applied successfully!</span>
          </div>
        )}
      </div>
      {/* Price Breakdown */}
      <div className="space-y-3 border-t border-border pt-4">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal ({items?.length} items)</span>
          <span className="font-medium">{formatDZ(subtotal)}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Shipping</span>
          <span className="font-medium">
            {shipping === 0 ? 'Free' : formatDZ(shipping)}
          </span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Tax</span>
          <span className="font-medium">{formatDZ(tax)}</span>
        </div>
        
        {discount > 0 && (
          <div className="flex justify-between text-sm text-success">
            <span>Discount</span>
            <span className="font-medium">-{formatDZ(discount)}</span>
          </div>
        )}
        
        <div className="border-t border-border pt-3">
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-primary">{formatDZ(total)}</span>
          </div>
        </div>
      </div>
      {/* Shipping Info */}
      <div className="bg-muted/20 p-4 rounded-lg space-y-2">
        <div className="flex items-center space-x-2 text-success">
          <Icon name="Truck" size={16} />
          <span className="text-sm font-medium">Free shipping on orders over DZ 6,750</span>
        </div>
        <div className="flex items-center space-x-2 text-muted-foreground">
          <Icon name="Calendar" size={16} />
          <span className="text-sm">Estimated delivery: 3-5 business days</span>
        </div>
      </div>
      {/* Checkout Button */}
      <Button
        variant="default"
        size="lg"
        fullWidth
        onClick={handleCheckout}
        disabled={items?.length === 0}
        className="trust-seal"
      >
        <Icon name="ShoppingBag" size={20} className="mr-2" />
        Proceed to Checkout
      </Button>
      {/* Trust Signals */}
      <div className="flex items-center justify-center space-x-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
            <Icon name="Shield" size={16} color="var(--color-success)" />
          </div>
          <span className="text-xs text-muted-foreground">SSL Secured</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="trust-seal p-2 bg-primary/10 border border-primary/30 rounded-lg">
            <Icon name="CreditCard" size={16} color="var(--color-primary)" />
          </div>
          <span className="text-xs text-muted-foreground">Secure Payment</span>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;